/**
 * LeadsV2 - Unified leads page with custom fetch support and URL state management
 * Combines functionality from /dashboard/leads and /leads pages
 * Supports navigation state preservation
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Menu, MenuItem, ListItemText, Button, Badge, Typography, Divider } from '@mui/material';
import {
  PersonAdd as AssignIcon,
  LocalOffer as TagIcon,
  NoteAdd as NoteAddIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  AddCircle as AddCircleIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

// Utils
import { LEAD_THEME as T, isClosureStageLead } from '../../utils/lead_helper_v2';
// Components
import LeadsTableV2, { ALL_COLUMNS } from './components/LeadsTableV2';
import LeadsToolbarV2 from './components/LeadsToolbarV2';
import LeadFilters, { hasMeaningfulFilters } from './LeadFilters';
import SaveLeadSearchDialog from './components/SaveLeadSearchDialog';
import { FILTER_CONFIGURATIONS } from './LeadSidebarV2';
import LeadsListViewV2 from './LeadsListViewV2';
import LeadFunnelStats from './LeadFunnelStats';
import ColumnCustomizer from './components/ColumnCustomizer';

// Dialogs
import ReassignAgent from '../../components/common/ReassignAgent';
import TagComponent from '../../components/common/Tags';
import AddNotes from '../../components/common/AddNotes';
import UpdateStatus from '../../components/common/UpdateStatus';
import LeadDelete from '../../components/common/LeadDelete';
import LeadRestore from '../../components/common/LeadRestore';
import CSVImport from '../../components/common/CSVImport';
import CSVUpdateLeadSource from './CSVUpdateLeadSource';
import CreateNewLead from './CreateNewLead';

// Hooks
import useLeadsV2 from './hooks/useLeadsV2';
import { fetchAgentSelect, fetchLeadTags, fetchProjectUnitSelect, fetchLeadsDefaultFilters } from '../../services/common';
import { useLead } from '../../contexts/LeadContext';

// Map API filter key (from status-counts) -> backend status value for sub-filters
const API_KEY_TO_BACKEND_STATUS = {
  won: 'closed_won',
  not_looking: 'not_looking_currently',
};

function filtersFromDynamicStatusId(statusId) {
  if (FILTER_CONFIGURATIONS[statusId]) return null; // use FILTER_CONFIGURATIONS
  const idx = statusId.indexOf('_');
  if (idx <= 0) return null;
  const stageKey = statusId.slice(0, idx);
  const apiKey = statusId.slice(idx + 1);
  if (stageKey === 'all') return { filters: {}, isDeleted: false };
  if (stageKey === 'unassigned') return { filters: { unassigned: true }, isDeleted: false };
  if (stageKey === 'deleted') return { filters: {}, isDeleted: true };
  if (apiKey === 'all') return { filters: { lead_stage: [stageKey] }, isDeleted: false };
  const backendStatus = API_KEY_TO_BACKEND_STATUS[apiKey] ?? apiKey;
  const filters = { lead_stage: [stageKey], status: [backendStatus] };
  if (stageKey === 'new' && apiKey === 'assigned') filters.last_contacted_at__isnull = true;
  return { filters, isDeleted: false };
}

/**
 * Helper function to match current state against sidebar configurations
 * Returns the sidebar status ID that matches the current state
 */
const matchFiltersToSidebarStatus = (filters, isDeleted) => {
  console.log('[matchFiltersToSidebarStatus] Input filters:', JSON.stringify(filters), 'isDeleted:', isDeleted);

  // Check for deleted (isDeleted is separate from filters)
  if (isDeleted === true) {
    console.log('[matchFiltersToSidebarStatus] Matched deleted, returning: deleted');
    return 'deleted';
  }

  if (!filters || Object.keys(filters).length === 0) {
    console.log('[matchFiltersToSidebarStatus] No filters, returning: all');
    return 'all';
  }

  // Check for unassigned
  if (filters.unassigned === true) {
    console.log('[matchFiltersToSidebarStatus] Matched unassigned, returning: unassigned');
    return 'unassigned';
  }

  // Check for specific sub-filters (more specific checks first)
  if (filters.lead_stage && filters.lead_stage.length === 1 && filters.status) {
    const stage = filters.lead_stage[0];
    const status = Array.isArray(filters.status) ? filters.status : [filters.status];

    // New stage sub-filters
    if (stage === 'new' && status.includes('assigned') && filters.last_contacted_at__isnull === true) {
      console.log('[matchFiltersToSidebarStatus] Matched new_assigned, returning: new_assigned');
      return 'new_assigned';
    }

    // Closure stage sub-filters
    if (stage === 'closure' && status.includes('closed_won')) {
      console.log('[matchFiltersToSidebarStatus] Matched closure_won, returning: closure_won');
      return 'closure_won';
    }
    if (stage === 'closure' && status.includes('lost')) {
      console.log('[matchFiltersToSidebarStatus] Matched closure_lost, returning: closure_lost');
      return 'closure_lost';
    }

    // Junk stage sub-filters
    if (stage === 'junk') {
      if (status.includes('not_interested')) return 'junk_not_interested';
      if (status.includes('not_looking_currently')) return 'junk_not_looking';
      if (status.includes('cold_lead')) return 'junk_cold_lead';
    }

    // Dynamic: any other stage + status -> sidebar id is stage_apiKey (API key from backend status)
    const backendToApiKey = { closed_won: 'won', not_looking_currently: 'not_looking' };
    const apiKey = backendToApiKey[status[0]] ?? status[0];
    return `${stage}_${apiKey}`;
  }

  // Check for main stage filters (only stage filter, no status)
  if (filters.lead_stage && filters.lead_stage.length === 1) {
    const stage = filters.lead_stage[0];
    const validStages = ['new', 'contacted', 'qualified', 'site_visit', 'negotiation', 'closure', 'junk', 'archived'];
    if (validStages.includes(stage)) {
      console.log(`[matchFiltersToSidebarStatus] Matched stage: ${stage}, returning: ${stage}`);
      return stage;
    }
  }

  // Default to 'all' if no specific match
  console.log('[matchFiltersToSidebarStatus] No match found, returning: all');
  return 'all';
};

// ---------------------------------------------------------------------------
// Filter comparison helpers (module-level — no component state dependencies)
// ---------------------------------------------------------------------------

/** Return a copy of filters without the `source` key */
const withoutSource = (filters = {}) => {
  const { source, ...rest } = filters || {};
  return rest;
};

// Helper function to safely compare filters (avoiding circular references)
const compareFilters = (current, previous) => {
  if (!current && !previous) return true;
  if (!current || !previous) return false;

  const currentKeys = Object.keys(current);
  const prevKeys = Object.keys(previous);

  if (currentKeys.length !== prevKeys.length) return false;

  return currentKeys.every((key) => {
    const currentVal = current[key];
    const prevVal = previous[key];

    // Handle arrays
    if (Array.isArray(currentVal) && Array.isArray(prevVal)) {
      if (currentVal.length !== prevVal.length) return false;
      return currentVal.every((item, idx) => {
        if (typeof item === 'object' && item !== null && item.value !== undefined) {
          return item.value === prevVal[idx]?.value;
        }
        return item === prevVal[idx];
      });
    }

    // Handle objects (like budget, last_contacted)
    if (
      typeof currentVal === 'object' && currentVal !== null &&
      typeof prevVal === 'object' && prevVal !== null
    ) {
      // For budget objects
      if (currentVal.min_value !== undefined || currentVal.max_value !== undefined) {
        return currentVal.min_value === prevVal.min_value &&
               currentVal.max_value === prevVal.max_value;
      }
      // For last_contacted objects
      if (currentVal.preset !== undefined) {
        return currentVal.preset === prevVal.preset &&
               currentVal.start_time === prevVal.start_time &&
               currentVal.end_time === prevVal.end_time;
      }
      // For boolean values (like unassigned, last_contacted_at__isnull)
      if (typeof currentVal === 'boolean' && typeof prevVal === 'boolean') {
        return currentVal === prevVal;
      }
      // For other objects, do shallow key comparison
      const currentObjKeys = Object.keys(currentVal);
      const prevObjKeys = Object.keys(prevVal);
      if (currentObjKeys.length !== prevObjKeys.length) return false;
      return currentObjKeys.every((objKey) => currentVal[objKey] === prevVal[objKey]);
    }

    // Primitive comparison
    return currentVal === prevVal;
  });
};

/** True when non-source filters are identical between current and previous */
const onlySourceChanged = (current = {}, previous = {}) =>
  compareFilters(withoutSource(current), withoutSource(previous));

// ---------------------------------------------------------------------------

const LeadsV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Context for status counts and sidebar
  const { statusCounts, fetchStatusCounts, setStatusCounts, setAppliedFilters } = useLead();

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const searchDebounceRef = useRef(null);

  // UI State
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [saveSearchOpen, setSaveSearchOpen] = useState(false);
  const [saveSearchFilters, setSaveSearchFilters] = useState(null);
  const [lastSavedFilters, setLastSavedFilters] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [bulkMenuAnchorEl, setBulkMenuAnchorEl] = useState(null);
  const [columnCustomizerOpen, setColumnCustomizerOpen] = useState(false);

  // Load visible columns from localStorage or use defaults
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem('leadsV2_visibleColumns');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading column preferences:', error);
    }
    // Default columns
    return [
      'lead_id', 'name', 'email', 'mobile', 'created_at', 'last_contacted_at',
      'stage', 'status', 'source', 'sales_owner', 'next_followup', 'next_visit',
    ];
  });

  // Dialog States
  const [dialogStates, setDialogStates] = useState({
    createLead: false,
    assignAgent: false,
    tags: false,
    notes: false,
    status: false,
    delete: false,
    restore: false,
    csvImport: false,
    csvUpdateSource: false,
  });

  // Data States
  const [agents, setAgents] = useState([]);
  const [leadTags, setLeadTags] = useState([]);
  const [projectUnits, setProjectUnits] = useState([]);
  const [defaultFilters, setDefaultFilters] = useState({});
  const [selectedLead, setSelectedLead] = useState(null);
  const [dateRangeState, setDateRangeState] = useState({
    filter: { id: 'All', label: 'All' },
    currentStart: null,
    currentEnd: null,
    all_results: true,
  });

  // Custom hook for state management (must be after dateRangeState is defined)
  const {
    state,
    leads,
    isLoading,
    error,
    paginationInfo,
    handlePageChange,
    handlePageSizeChange,
    handleSort,
    handleFiltersChange,
    handleRefresh,
    handleExport,
    updateState,
    isCustomFetchMode,
  } = useLeadsV2(dateRangeState, visibleColumns);

  // Derive the selected sidebar status from URL state or match from filters
  const selectedSidebarStatus = state.sidebarStatus || matchFiltersToSidebarStatus(state.filters, state.isDeleted);
  console.log('[LeadsV2] selectedSidebarStatus:', selectedSidebarStatus, 'sidebarStatus from URL:', state.sidebarStatus, 'filters:', state.filters, 'isDeleted:', state.isDeleted);

  // Fetch initial data
  useEffect(() => {
    Promise.all([
      fetchAgentSelect().then(setAgents),
      fetchLeadTags().then(setLeadTags),
      fetchProjectUnitSelect().then(setProjectUnits),
      fetchLeadsDefaultFilters().then(setDefaultFilters),
    ]).catch(console.error);
  }, []);

  // Track previous date range/filters for status-counts
  const prevFiltersRef = useRef(null);
  const prevDateRangeRef = useRef(null);
  const hasFetchedStatusCountsRef = useRef(false);
  const statusCountsRequestSeqRef = useRef(0);

  // Helper function to safely compare date range state
  const compareDateRange = (current, previous) => {
    if (!current && !previous) return true;
    if (!current || !previous) return false;

    const currentFilterId = current.filter?.id;
    const prevFilterId = previous?.filter?.id;
    const currentStart = current.currentStart?.format('YYYY-MM-DD');
    const prevStart = previous?.currentStart;
    const currentEnd = current.currentEnd?.format('YYYY-MM-DD');
    const prevEnd = previous?.currentEnd;
    const currentAllResults = current.all_results;
    const prevAllResults = previous?.all_results;

    return (
      currentFilterId === prevFilterId &&
      currentStart === prevStart &&
      currentEnd === prevEnd &&
      currentAllResults === prevAllResults
    );
  };

  // Status counts (pipeline/source/saved searches):
  // Refetch on date range and non-source filter changes.
  // Skips the fetch when ONLY the source filter changed.
  // Debounced so date-range init + filter churn in one paint issues one request.
  useEffect(() => {
    if (isCustomFetchMode) return;

    const t = window.setTimeout(() => {
    const dateRangeChanged = !compareDateRange(dateRangeState, prevDateRangeRef.current);
    const isFirstFetch = !hasFetchedStatusCountsRef.current;

    // Skip fetch when only the source filter changed (non-source filters are identical)
    if (
      !isFirstFetch &&
      !dateRangeChanged &&
      onlySourceChanged(state.filters, prevFiltersRef.current)
    ) {
      // Still update the ref so future comparisons have the latest source value
      prevFiltersRef.current = state.filters;
      return;
    }

    const nonSourceFiltersChanged = !compareFilters(
      withoutSource(state.filters),
      withoutSource(prevFiltersRef.current),
    );

    if (isFirstFetch || dateRangeChanged || nonSourceFiltersChanged) {
      hasFetchedStatusCountsRef.current = true;

      const reqSeq = ++statusCountsRequestSeqRef.current;
      fetchStatusCounts(dateRangeState, withoutSource(state.filters), state.isDeleted)
        .then((data) => {
          if (reqSeq !== statusCountsRequestSeqRef.current) return;
          if (data) {
            setStatusCounts((prev) => {
              const next = { ...prev, ...data };
              if (data.saved_searches !== undefined) next.saved_searches = data.saved_searches;
              Object.keys(FILTER_CONFIGURATIONS).forEach((key) => {
                if (FILTER_CONFIGURATIONS[key].type === 'sub') return;
                if (next[key] === undefined && prev[key] != null) next[key] = prev[key];
              });
              return next;
            });
          }
        })
        .catch((error) => {
          console.error('Error fetching status counts:', error);
        });

      // Store full filters (including source) so next comparison is accurate
      prevFiltersRef.current = state.filters;
      prevDateRangeRef.current = dateRangeState
        ? {
            filter: dateRangeState.filter ? { ...dateRangeState.filter } : null,
            currentStart: dateRangeState.currentStart?.format('YYYY-MM-DD') || null,
            currentEnd: dateRangeState.currentEnd?.format('YYYY-MM-DD') || null,
            all_results: dateRangeState.all_results,
          }
        : null;
    }
    }, 150);

    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRangeState, state.filters, isCustomFetchMode]);

  // Stable refs for the CSV import handler to avoid effect re-runs
  const fetchStatusCountsRef = useRef(fetchStatusCounts);
  const dateRangeStateRef = useRef(dateRangeState);
  const setStatusCountsRef = useRef(setStatusCounts);
  const filtersRef = useRef(state.filters);
  const isDeletedRef = useRef(state.isDeleted);
  useEffect(() => { fetchStatusCountsRef.current = fetchStatusCounts; }, [fetchStatusCounts]);
  useEffect(() => { dateRangeStateRef.current = dateRangeState; }, [dateRangeState]);
  useEffect(() => { setStatusCountsRef.current = setStatusCounts; }, [setStatusCounts]);
  useEffect(() => { filtersRef.current = state.filters; }, [state.filters]);
  useEffect(() => { isDeletedRef.current = state.isDeleted; }, [state.isDeleted]);

  // Refresh leads list and status counts when CSV import completes (success or partial)
  useEffect(() => {
    const onImportCompleted = () => {
      handleRefresh();
      fetchStatusCountsRef.current(
        dateRangeStateRef.current,
        withoutSource(filtersRef.current),
        isDeletedRef.current,
      )
        .then((data) => {
          if (data) setStatusCountsRef.current(data);
        })
        .catch((err) => console.error('Error refreshing status counts after import:', err));
    };
    window.addEventListener('leadImportCompleted', onImportCompleted);
    return () => window.removeEventListener('leadImportCompleted', onImportCompleted);
  }, [handleRefresh]);


  // Dialog handlers — row actions set `selectedLead` only; do not mutate checkbox selection (avoids bulk bar)
  const openDialog = (dialogName, lead = null) => {
    setSelectedLead(lead);
    setDialogStates((prev) => ({ ...prev, [dialogName]: true }));
  };

  const closeDialog = (dialogName) => {
    setDialogStates((prev) => ({ ...prev, [dialogName]: false }));
    setSelectedLead(null);
  };

  // Selection handlers (closure-stage leads cannot be bulk-selected)
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedLeads(leads.filter((lead) => !isClosureStageLead(lead)).map((lead) => lead.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (leadId) => {
    const row = leads.find((l) => l.id === leadId);
    setSelectedLeads((prev) => {
      if (prev.includes(leadId)) {
        return prev.filter((id) => id !== leadId);
      }
      if (row && isClosureStageLead(row)) {
        return prev;
      }
      return [...prev, leadId];
    });
  };

  // Bulk actions
  const handleBulkAction = (action) => {
    setBulkMenuAnchorEl(null);
    switch (action) {
      case 'assign':
        openDialog('assignAgent');
        break;
      case 'tags':
        openDialog('tags');
        break;
      case 'notes':
        openDialog('notes');
        break;
      case 'status':
        openDialog('status');
        break;
      case 'delete':
        openDialog('delete');
        break;
      case 'restore':
        openDialog('restore');
        break;
      default:
        break;
    }
  };

  // Action completion handlers
  const handleActionComplete = () => {
    handleRefresh();
    setSelectedLeads([]);
    Object.keys(dialogStates).forEach((key) => {
      closeDialog(key);
    });
    // Refresh sidebar counts so deleted/total counters update immediately
    fetchStatusCounts(dateRangeState, withoutSource(state.filters), state.isDeleted)
      .then((data) => {
        if (data) setStatusCounts((prev) => ({ ...prev, ...data }));
      })
      .catch((err) => console.error('Error refreshing status counts after action:', err));
  };

  // Get custom fetch title
  const getCustomFetchTitle = () => {
    const statTypeTitles = {
      'total-leads': 'Total Leads',
      'new-leads': 'New Leads',
      'unassigned-leads': 'Unassigned Leads',
      'untouched-leads': 'Untouched Leads',
      'no-reminder-leads': 'Leads with No Reminder',
      'site-visit-scheduled': 'Site Visit Scheduled',
      'site-visit-confirmed': 'Site Visit Confirmed',
      'site-visit-done': 'Site Visit Done',
      'not-interested-leads': 'Leads Marked Not Interested',
      'dormant-leads': 'Hot/Warm Leads at Risk',
      'lead-contacted': 'Leads Contacted',
      'bookings-closed': 'Bookings Closed',
    };
    return statTypeTitles[state.statType] || 'Leads';
  };

  // Unified handler that syncs both useLeadsV2 state and LeadContext
  const handleFiltersChangeWithContext = useCallback((newFilters) => {
    handleFiltersChange(newFilters);
    setAppliedFilters?.(newFilters || {});
    setLastSavedFilters(null); // filters changed — allow saving again
  }, [handleFiltersChange, setAppliedFilters]);

  // Search handler — toolbar debounces 500ms before calling this, so apply immediately
  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
    const nextFilters = { ...state.filters };
    if (value && value.trim()) {
      nextFilters.search = value.trim();
    } else {
      delete nextFilters.search;
    }
    handleFiltersChangeWithContext(nextFilters);
  }, [state.filters, handleFiltersChangeWithContext]);

  // Handle sidebar status filter changes (PipelineSidebar)
  const handleStatusFilterChange = (filters, statusId) => {
    const isDeletedFilter = filters.is_deleted === true;
    const pipelineFilters = { ...filters };
    delete pipelineFilters.is_deleted;

    updateState({
      page: 1,
      pipelineFilters,          // stage/status — ANDed with user filters
      isDeleted: isDeletedFilter,
      sidebarStatus: statusId === 'all' ? null : statusId,
    });
  };

  // Handle sidebar status change from LeadSidebarV2
  const handleSidebarV2StatusChange = (statusId) => {
    let pipelineFilters = {};
    let isDeletedFilter = false;

    const filterConfig = FILTER_CONFIGURATIONS[statusId];
    if (filterConfig) {
      isDeletedFilter = filterConfig.filters.is_deleted === true;
      pipelineFilters = { ...filterConfig.filters };
      delete pipelineFilters.is_deleted;
    } else {
      const dynamic = filtersFromDynamicStatusId(statusId);
      if (!dynamic) return;
      isDeletedFilter = dynamic.isDeleted;
      pipelineFilters = { ...dynamic.filters };
    }

    updateState({
      page: 1,
      pipelineFilters,          // stage/status — ANDed with user filters
      isDeleted: isDeletedFilter,
      sidebarStatus: statusId === 'all' ? null : statusId,
    });
  };

  const handleSourceFilterChange = (sourceValue) => {
    const nextSource = sourceValue == null || sourceValue === '' ? undefined : [sourceValue];
    const nextFilters = { ...state.filters };
    if (nextSource) {
      nextFilters.source = nextSource;
    } else {
      delete nextFilters.source;
    }
    updateState({
      page: 1,
      filters: nextFilters,
    });

    // Keep filter modal in sync with source funnel selection
    setAppliedFilters?.(nextFilters);
  };

  // Apply saved search from sidebar (LeadsV2) using /lms/leads/v2/
  const handleApplySavedSearchV2 = (search) => {
    if (!search || !search.search_criteria) return;

    try {
      const rawCriteria = search.search_criteria || {};
      const searchCriteria = { ...rawCriteria };

      // Normalize budget fields if bookmark stored min/max separately
      if (
        (searchCriteria.budget_min !== undefined || searchCriteria.budget_max !== undefined) &&
        !searchCriteria.budget
      ) {
        searchCriteria.budget = {
          min_value: searchCriteria.budget_min || 0,
          max_value: searchCriteria.budget_max || 100000000,
        };
        delete searchCriteria.budget_min;
        delete searchCriteria.budget_max;
      }

      // Normalize last_contacted fields if stored separately
      if (
        (searchCriteria.last_contacted_from !== undefined ||
          searchCriteria.last_contacted_to !== undefined) &&
        !searchCriteria.last_contacted
      ) {
        searchCriteria.last_contacted = {
          start_time: searchCriteria.last_contacted_from,
          end_time: searchCriteria.last_contacted_to,
        };
        delete searchCriteria.last_contacted_from;
        delete searchCriteria.last_contacted_to;
      }

      // Ensure array fields are arrays
      const arrayFields = [
        'status',
        'lead_stage',
        'tags',
        'assigned_to',      // canonical agent key
        'agent_id',         // legacy, will be normalized
        'agents',           // legacy, will be normalized
        'source',
        'bhk_type',
        'lead_urgency',
        'lead_lcp',
        'occupation',
        'site_visit_frequency',
        'channel_partner',
        'channel_partners',
      ];
      arrayFields.forEach((field) => {
        if (searchCriteria[field] !== undefined && !Array.isArray(searchCriteria[field])) {
          searchCriteria[field] = [searchCriteria[field]];
        }
      });

      // Normalize legacy agent keys to canonical `assigned_to` only
      // - Bookmarks now store `assigned_to`
      // - Older bookmarks might have `agents` or `agent_id`
      if (!searchCriteria.assigned_to) {
        if (Array.isArray(searchCriteria.agents) && searchCriteria.agents.length > 0) {
          searchCriteria.assigned_to = searchCriteria.agents;
        } else if (Array.isArray(searchCriteria.agent_id) && searchCriteria.agent_id.length > 0) {
          searchCriteria.assigned_to = searchCriteria.agent_id;
        }
      }
      // Drop legacy aliases so payload uses only `assigned_to`
      delete searchCriteria.agents;
      delete searchCriteria.agent_id;

      // Apply filters via useLeadsV2 state; this triggers /lms/leads/v2/ fetch
      updateState({
        page: 1,
        filters: searchCriteria,
        isDeleted: false,
        sidebarStatus: `search_${search.id}`,
      });

      // Also sync into LeadFilters modal/context so fields show as selected
      setAppliedFilters?.(searchCriteria);
    } catch (err) {
      console.error('Error applying saved search in LeadsV2:', err);
    }
  };

  // Handle funnel stats stage click
  const handleFunnelStageClick = (stageKey) => {
    handleSidebarV2StatusChange(stageKey);
  };

  /** IDs eligible for bulk bar / bulk API (closure-stage leads excluded) */
  const bulkActionLeadIds = useMemo(
    () =>
      selectedLeads.filter((id) => {
        const row = leads.find((l) => l.id === id);
        return row && !isClosureStageLead(row);
      }),
    [selectedLeads, leads],
  );

  /**
   * IDs targeted by open dialogs: explicit row from action menu first, then bulk checkbox selection.
   * Row actions must not depend on `selectedLeads` (checkboxes stay unchanged when using the ⋮ menu).
   */
  const leadIdsForDialogs = useMemo(() => {
    if (selectedLead?.id != null) return [selectedLead.id];
    if (bulkActionLeadIds.length > 0) return bulkActionLeadIds;
    return selectedLeads;
  }, [selectedLead, bulkActionLeadIds, selectedLeads]);

  /** Lead shown in ReassignAgent header (single selection, or first row when multiple) */
  const reassignContextLead = useMemo(() => {
    if (!leadIdsForDialogs.length) return null;
    if (leadIdsForDialogs.length === 1) {
      return selectedLead || leads.find((l) => l.id === leadIdsForDialogs[0]) || null;
    }
    return leads.find((l) => l.id === leadIdsForDialogs[0]) || null;
  }, [leadIdsForDialogs, selectedLead, leads]);

  const reassignContextAgent =
    reassignContextLead &&
    (reassignContextLead.agent ||
      reassignContextLead.assigned_agent ||
      (reassignContextLead.agent_id
        ? {
            id: reassignContextLead.agent_id,
            agent_id: reassignContextLead.agent_id,
            name: reassignContextLead.agent_name,
          }
        : null));

  const reassignDialogLeadName =
    leadIdsForDialogs.length > 1
      ? `${leadIdsForDialogs.length} selected leads`
      : reassignContextLead?.contact?.name || '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', height: '100vh', overflow: 'hidden' }}>
      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        {/* Toolbar — date, filters, pagination */}
        <Box sx={{ position: 'sticky', top: 0, zIndex: 100, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
          <LeadsToolbarV2
            onFilterClick={(e) => setFilterAnchorEl(e.currentTarget)}
            onBack={isCustomFetchMode ? () => navigate('/dashboard') : null}
            appliedFilters={state.filters}
            onFiltersChange={handleFiltersChangeWithContext}
            onResetFilters={() => {
              setSearchQuery('');
              updateState({
                page: 1,
                filters: {},
                isDeleted: false,
                sidebarStatus: null,
              });
              setAppliedFilters?.({});
            }}
            defaultFilters={defaultFilters}
            page={state.page}
            rowsPerPage={state.pageSize}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handlePageSizeChange}
            paginationInfo={paginationInfo}
            isCustomFetchMode={isCustomFetchMode}
            customFetchTitle={getCustomFetchTitle()}
            dateRangeState={dateRangeState}
            onDateRangeChange={setDateRangeState}
            onSaveSearchClick={() => {
              if (!hasMeaningfulFilters(state.filters)) return;
              setSaveSearchFilters(state.filters);
              setSaveSearchOpen(true);
            }}
            canSaveSearch={hasMeaningfulFilters(state.filters) && !compareFilters(state.filters, lastSavedFilters)}
            searchValue={searchQuery}
            onSearchChange={handleSearchChange}
          />
        </Box>

        {/* Bulk Actions Bar — white strip: selection | Bulk Actions | Clear (inline) */}
        {bulkActionLeadIds.length > 0 && (
          <Box
            sx={{
              mx: 2,
              mt: 1,
              mb: 0.5,
              px: 2,
              py: 1.25,
              borderRadius: '12px',
              bgcolor: '#fff',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.5,
              position: 'sticky',
              top: 0,
              zIndex: 99,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '6px',
                  bgcolor: '#16a34a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
                aria-hidden
              >
                <CheckIcon sx={{ fontSize: 17, color: '#fff' }} />
              </Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>
                {bulkActionLeadIds.length} selected
              </Typography>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ borderColor: '#e2e8f0', height: 26, alignSelf: 'center' }} />

            <Button
              variant="contained"
              size="small"
              startIcon={<AddCircleIcon sx={{ fontSize: 20, color: '#fff' }} />}
              endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 20, color: '#fff' }} />}
              onClick={(e) => setBulkMenuAnchorEl(e.currentTarget)}
              sx={{
                height: 36,
                px: 1.75,
                borderRadius: '8px',
                fontSize: '0.8125rem',
                fontWeight: 600,
                bgcolor: '#16a34a',
                boxShadow: 'none',
                textTransform: 'none',
                '&:hover': { bgcolor: '#15803d', boxShadow: 'none' },
              }}
            >
              Bulk Actions
            </Button>

            <Button
              variant="text"
              size="small"
              endIcon={<CloseIcon sx={{ fontSize: 16, color: '#64748b' }} />}
              onClick={() => setSelectedLeads([])}
              sx={{
                height: 36,
                px: 1,
                color: '#64748b',
                fontSize: '0.875rem',
                fontWeight: 500,
                textTransform: 'none',
                flexShrink: 0,
                '&:hover': { bgcolor: 'rgba(148, 163, 184, 0.12)', color: '#475569' },
              }}
            >
              Clear
            </Button>
          </Box>
        )}

        {/* Leads List View — sidebar (Pipeline) + table */}
        <Box sx={{ flex: 1, overflow: 'hidden', p: 1, minHeight: 0 }}>
          <LeadsListViewV2
            leads={leads}
            isLoading={isLoading}
            page={state.page}
            rowsPerPage={state.pageSize}
            totalCount={paginationInfo.total_count ?? 0}
            totalPages={paginationInfo.total_pages ?? 1}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handlePageSizeChange}
            hideTablePagination
            onCreateLead={() => openDialog('createLead')}
            onImport={() => openDialog('csvImport')}
            onSourceUpdate={() => openDialog('csvUpdateSource')}
            statusCounts={statusCounts || {}}
            activeStageFilter={selectedSidebarStatus || 'all'}
            onStageFilterChange={handleSidebarV2StatusChange}
            onExport={handleExport}
            onColumnCustomize={() => setColumnCustomizerOpen(true)}
            selectedLeads={selectedLeads}
            onSelectAll={handleSelectAll}
            onSelectLead={handleSelectLead}
            visibleColumns={visibleColumns}
            onAction={openDialog}
            isDeleted={false}
            savedSearches={statusCounts?.saved_searches || []}
            onApplySavedSearch={handleApplySavedSearchV2}
            activeSavedSearchId={
              state.sidebarStatus?.startsWith?.('search_')
                ? state.sidebarStatus.replace('search_', '')
                : null
            }
            activeSourceFilter={state.filters?.source?.[0]}
            onSourceFilterChange={handleSourceFilterChange}
            defaultFilters={defaultFilters}
            hideSidebar={Boolean(state.statType)}
          />
        </Box>
      </Box>

      {/* Filters Dialog */}
      <LeadFilters
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        defaultFilters={defaultFilters}
        agents={agents}
        statusCounts={statusCounts}
        setStatusCounts={setStatusCounts}
        dateRangeState={dateRangeState}
        onFiltersChange={handleFiltersChangeWithContext}
        onOpenSaveSearch={(draftFilters) => {
          if (!hasMeaningfulFilters(draftFilters)) return;
          setSaveSearchFilters(draftFilters);
          setSaveSearchOpen(true);
        }}
      />

      <SaveLeadSearchDialog
        open={saveSearchOpen}
        onClose={() => {
          setSaveSearchOpen(false);
          setSaveSearchFilters(null);
          setFilterAnchorEl(null);
        }}
        onSaved={(savedFilters) => setLastSavedFilters(savedFilters)}
        filters={saveSearchFilters}
        defaultFilters={defaultFilters}
        agents={agents}
      />

      {/* Bulk Actions Menu — icon tiles + title / subtitle rows */}
      <Menu
        anchorEl={bulkMenuAnchorEl}
        open={Boolean(bulkMenuAnchorEl)}
        onClose={() => setBulkMenuAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 300,
              borderRadius: '12px',
              bgcolor: '#fff',
              boxShadow: '0 4px 24px rgba(15, 23, 42, 0.12)',
              border: '1px solid #e2e8f0',
              mt: 1.5,
              overflow: 'hidden',
            },
          },
        }}
        MenuListProps={{ sx: { py: 0 } }}
      >
        <MenuItem
          onClick={() => handleBulkAction('assign')}
          sx={{
            py: 1.5,
            px: 2,
            gap: 1.5,
            alignItems: 'flex-start',
            '&:hover': { bgcolor: '#f8fafc' },
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              bgcolor: '#d1fae5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <AssignIcon sx={{ fontSize: 22, color: '#059669' }} />
          </Box>
          <ListItemText
            primary="Reassign agent"
            secondary="New owner, stage, and status for selected leads"
            primaryTypographyProps={{ fontWeight: 700, fontSize: '0.875rem', color: '#0f172a', lineHeight: 1.35 }}
            secondaryTypographyProps={{ fontSize: '0.75rem', color: '#64748b', lineHeight: 1.45, mt: 0.25 }}
          />
        </MenuItem>
        <Divider sx={{ my: 0, borderColor: '#e2e8f0' }} />
        <MenuItem
          onClick={() => handleBulkAction('tags')}
          sx={{
            py: 1.5,
            px: 2,
            gap: 1.5,
            alignItems: 'flex-start',
            '&:hover': { bgcolor: '#f8fafc' },
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              bgcolor: '#d1fae5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <TagIcon sx={{ fontSize: 22, color: '#059669' }} />
          </Box>
          <ListItemText
            primary="Manage tags"
            secondary="Add or edit tags for selected leads"
            primaryTypographyProps={{ fontWeight: 700, fontSize: '0.875rem', color: '#0f172a', lineHeight: 1.35 }}
            secondaryTypographyProps={{ fontSize: '0.75rem', color: '#64748b', lineHeight: 1.45, mt: 0.25 }}
          />
        </MenuItem>
        <Divider sx={{ my: 0, borderColor: '#e2e8f0' }} />
        <MenuItem
          onClick={() => handleBulkAction('notes')}
          sx={{
            py: 1.5,
            px: 2,
            gap: 1.5,
            alignItems: 'flex-start',
            '&:hover': { bgcolor: '#f8fafc' },
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              bgcolor: '#d1fae5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <NoteAddIcon sx={{ fontSize: 22, color: '#059669' }} />
          </Box>
          <ListItemText
            primary="Add notes"
            secondary="Add a note to selected leads"
            primaryTypographyProps={{ fontWeight: 700, fontSize: '0.875rem', color: '#0f172a', lineHeight: 1.35 }}
            secondaryTypographyProps={{ fontSize: '0.75rem', color: '#64748b', lineHeight: 1.45, mt: 0.25 }}
          />
        </MenuItem>
        {!state.isDeleted && (
          <>
            <Divider sx={{ my: 0, borderColor: '#e2e8f0' }} />
            <MenuItem
              onClick={() => handleBulkAction('delete')}
              sx={{
                py: 1.5,
                px: 2,
                gap: 1.5,
                alignItems: 'flex-start',
                '&:hover': { bgcolor: '#fef2f2' },
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  bgcolor: '#fecaca',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <DeleteIcon sx={{ fontSize: 22, color: '#b91c1c' }} />
              </Box>
              <ListItemText
                primary="Delete leads"
                secondary="Move selected leads to trash"
                primaryTypographyProps={{ fontWeight: 700, fontSize: '0.875rem', color: '#7f1d1d', lineHeight: 1.35 }}
                secondaryTypographyProps={{ fontSize: '0.75rem', color: '#991b1b', lineHeight: 1.45, mt: 0.25 }}
              />
            </MenuItem>
          </>
        )}
        {state.isDeleted && (
          <>
            <Divider sx={{ my: 0, borderColor: '#e2e8f0' }} />
            <MenuItem
              onClick={() => handleBulkAction('restore')}
              sx={{
                py: 1.5,
                px: 2,
                gap: 1.5,
                alignItems: 'flex-start',
                '&:hover': { bgcolor: '#f8fafc' },
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  bgcolor: '#d1fae5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <RestoreIcon sx={{ fontSize: 22, color: '#059669' }} />
              </Box>
              <ListItemText
                primary="Restore leads"
                secondary="Restore selected leads from trash"
                primaryTypographyProps={{ fontWeight: 700, fontSize: '0.875rem', color: '#0f172a', lineHeight: 1.35 }}
                secondaryTypographyProps={{ fontSize: '0.75rem', color: '#64748b', lineHeight: 1.45, mt: 0.25 }}
              />
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Dialogs */}
      <CreateNewLead
        open={dialogStates.createLead}
        defaultFilters={defaultFilters}
        agents={agents}
        onClose={() => closeDialog('createLead')}
        onLeadCreated={handleRefresh}
      />

      <ReassignAgent
        open={dialogStates.assignAgent}
        onClose={() => closeDialog('assignAgent')}
        selectedLeadIds={leadIdsForDialogs}
        currentAgent={reassignContextAgent}
        currentStage={reassignContextLead?.stage}
        currentStatus={reassignContextLead?.status}
        leadName={reassignDialogLeadName}
        leadId={reassignContextLead?.id ?? leadIdsForDialogs[0]}
        onComplete={handleActionComplete}
      />

      <TagComponent
        open={dialogStates.tags}
        onClose={() => closeDialog('tags')}
        tags={leadTags}
        selectedTags={selectedLead?.tags || []}
        onSave={handleActionComplete}
        title={
          selectedLead
            ? `Edit Tags for ${selectedLead.contact?.name}`
            : `Update Tags for ${leadIdsForDialogs.length} Selected Leads`
        }
      />

      <AddNotes
        open={dialogStates.notes}
        onClose={() => closeDialog('notes')}
        leadIds={leadIdsForDialogs}
        onComplete={handleActionComplete}
      />

      <UpdateStatus
        open={dialogStates.status}
        onClose={() => closeDialog('status')}
        leadIds={leadIdsForDialogs}
        currentStatus={selectedLead?.status}
        currentStage={selectedLead?.stage}
        leadName={selectedLead?.contact?.name || ''}
        leadId={selectedLead?.id || ''}
        onComplete={handleActionComplete}
        projectUnits={projectUnits}
      />

      <LeadDelete
        open={dialogStates.delete}
        onClose={() => closeDialog('delete')}
        leadIds={leadIdsForDialogs}
        leadName={selectedLead?.contact?.name || ''}
        leadId={selectedLead?.id || ''}
        leadDetails={leads.filter((lead) => leadIdsForDialogs.includes(lead.id))}
        onComplete={handleActionComplete}
      />

      <LeadRestore
        open={dialogStates.restore}
        onClose={() => closeDialog('restore')}
        leadIds={leadIdsForDialogs}
        leadName={leadIdsForDialogs.length > 1 ? 'All selected leads' : selectedLead?.contact?.name || ''}
        leadId={selectedLead?.id || ''}
        leadDetails={leads.filter((lead) => leadIdsForDialogs.includes(lead.id))}
        onComplete={handleActionComplete}
        onSuccess={() => {
          handleRefresh();
          fetchStatusCounts(dateRangeState, withoutSource(state.filters), state.isDeleted)
            .then((data) => {
              if (data) setStatusCounts((prev) => ({ ...prev, ...data }));
            })
            .catch((err) => console.error('Error refreshing status counts after restore:', err));
        }}
      />

      <CSVImport
        open={dialogStates.csvImport}
        onClose={() => closeDialog('csvImport')}
        onImportComplete={handleActionComplete}
        defaultFilters={defaultFilters}
      />

      <CSVUpdateLeadSource
        open={dialogStates.csvUpdateSource}
        onClose={() => closeDialog('csvUpdateSource')}
        onComplete={handleActionComplete}
      />

      {/* Column Customizer Dialog */}
      <ColumnCustomizer
        open={columnCustomizerOpen}
        onClose={() => {
          console.log('Closing column customizer dialog');
          setColumnCustomizerOpen(false);
        }}
        availableColumns={ALL_COLUMNS}
        visibleColumns={visibleColumns}
        onSave={(newColumns) => {
          console.log('Saving column preferences:', newColumns);
          setVisibleColumns(newColumns);
          try {
            localStorage.setItem('leadsV2_visibleColumns', JSON.stringify(newColumns));
            console.log('Column preferences saved to localStorage');
          } catch (error) {
            console.error('Error saving column preferences:', error);
          }
        }}
      />
    </Box>
  );
};

export default LeadsV2;
